import { decode as iconvDecode } from "iconv-lite-umd";
import Term from "../../term/Term";
import { parse } from "papaparse";
import i18n from "../../../i18n";

function tryDecodeUTF8Strict(buf: Uint8Array): string | null {
  try {
    // 유효하지 않은 UTF-8 시 throw (오검출 방지 핵심)
    return new TextDecoder("utf-8", { fatal: true }).decode(buf);
  } catch {
    return null;
  }
}

// 브라우저 TextDecoder가 지원하는지 체크
function canUseTextDecoder(label: string): boolean {
  try {
    // 일부 브라우저는 미지원 인코딩에 대해 생성 시 에러
    new TextDecoder(label);
    return true;
  } catch {
    return false;
  }
}

// BOM 스니핑
function sniffBom(buf: Uint8Array): { encoding?: string; bomBytes?: number } {
  if (
    buf.length >= 3 &&
    buf[0] === 0xef &&
    buf[1] === 0xbb &&
    buf[2] === 0xbf
  ) {
    return { encoding: "utf-8", bomBytes: 3 };
  }
  if (buf.length >= 2 && buf[0] === 0xff && buf[1] === 0xfe) {
    return { encoding: "utf-16le", bomBytes: 2 };
  }
  if (buf.length >= 2 && buf[0] === 0xfe && buf[1] === 0xff) {
    return { encoding: "utf-16be", bomBytes: 2 };
  }
  return {};
}

// 인코딩 라벨 정규화 (alias 정리)
function normalizeLabel(label: string): string {
  const l = label.trim().toLowerCase();

  // UTF
  if (["utf8", "utf-8"].includes(l)) return "utf-8";
  if (["utf16le", "utf-16le"].includes(l)) return "utf-16le";
  if (["utf16be", "utf-16be"].includes(l)) return "utf-16be";

  // 한국 CSV에서 자주 쓰이는 CP949 (MS949, windows-949)
  if (["cp949", "ms949", "windows-949", "x-windows-949"].includes(l))
    return "cp949";

  // 일본
  if (["shift_jis", "shift-jis", "sjis", "ms932", "windows-31j"].includes(l))
    return "shift_jis";

  // 기타 대표 single-byte들 예시 (필요시 추가)
  if (["iso-8859-1", "latin1"].includes(l)) return "iso-8859-1";
  if (["windows-1252", "cp1252"].includes(l)) return "windows-1252";

  return l;
}

function decodeToString(
  buf: Uint8Array,
  label: string,
  bomBytes: number
): string {
  const slice = bomBytes > 0 ? buf.subarray(bomBytes) : buf;

  // TextDecoder로 가능한 경우 우선 사용 (성능 좋고 BOM 자동 처리)
  if (canUseTextDecoder(label)) {
    return new TextDecoder(label).decode(slice);
  }

  // iconv-lite-umd로 폴백 (cp949, shift_jis 등)
  // iconv-lite는 Node Buffer를 기대하므로 Uint8Array도 정상 처리됨
  return iconvDecode(slice, label);
}

export async function parseCsvFileAuto(file: File): Promise<Term[]> {
  const arrayBuf = await file.arrayBuffer();
  const u8 = new Uint8Array(arrayBuf);

  let text = tryDecodeUTF8Strict(u8);
  let usedLabel = "utf-8";

  if (text == null) {
    //  BOM 스니핑
    const bom = sniffBom(u8);
    if (bom.encoding) {
      text = decodeToString(
        u8,
        normalizeLabel(bom.encoding),
        bom.bomBytes ?? 0
      );
      usedLabel = normalizeLabel(bom.encoding);
    } else {
      let hasHighBytes = false;
      const sample = u8.slice(0, Math.min(u8.length, 1000));

      for (let i = 0; i < sample.length; i++) {
        if (sample[i] >= 0x80) {
          hasHighBytes = true;
          break;
        }
      }

      const encodingsToTry = hasHighBytes
        ? ["cp949", "utf-8", "shift_jis", "utf-16le", "euc-kr"]
        : ["utf-8", "cp949", "shift_jis", "utf-16le"];

      for (const encoding of encodingsToTry) {
        try {
          const testText = decodeToString(u8, encoding, 0);

          // 간단한 유효성 검사: 제어 문자가 너무 많으면 잘못된 인코딩
          const preview = testText.slice(0, 500);
          const controlChars = (
            // eslint-disable-next-line no-control-regex
            preview.match(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g) || []
          ).length;
          const controlRatio = controlChars / preview.length;

          if (controlRatio < 0.05) {
            // 제어문자 5% 미만
            text = testText;
            usedLabel = encoding;
            break;
          }
        } catch {
          continue;
        }
      }

      if (!text) throw new Error("Wrong csv file.");
    }
  }

  const result = parse<Record<string, string>>(text!, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (h) => h.trim(),
    transform: (v) => (typeof v === "string" ? v.trim() : v),
  });

  let header = result.meta.fields ?? [];

  if (!hasTdpeHeader(header)) {
    if (usedLabel !== "utf-8") {
      for (const fb of ["utf-8", "cp949", "shift_jis"] as const) {
        try {
          const txt = decodeToString(u8, fb, 0);
          const re = parse<Record<string, string>>(txt, {
            header: true,
            skipEmptyLines: "greedy",
            transformHeader: (h) => h.trim(),
            transform: (v) => (typeof v === "string" ? v.trim() : v),
          });

          const h2 = re.meta.fields ?? [];
          if (hasTdpeHeader(h2)) {
            const terms = rowsToTerms(re.data);
            if (terms.length === 0) {
              throw new Error("ErrorCSVFileNoData");
            }
            return terms;
          }
        } catch {
          // 다음 후보 인코딩 시도
        }
      }
    }

    // 여기까지 왔으면 인코딩 재시도 후에도 여전히 헤더가 올바르지 않음
    throw new Error(i18n.t("WrongCSVHeaderErrorMessage"));
  }

  const terms = rowsToTerms(result.data);
  if (terms.length === 0) {
    throw new Error("ErrorCSVFileNoData");
  }
  return terms;
}

function rowsToTerms(rows: Record<string, string>[]): Term[] {
  const out: Term[] = [];
  // 🔹 키를 모두 소문자+trim 형태로 변환한 매핑 테이블 생성
  for (const row of rows) {
    const normalized: Record<string, string> = {};
    for (const [key, value] of Object.entries(row)) {
      normalized[key.trim().toLowerCase()] = value;
    }

    const term = (normalized["t"] ?? "").trim();
    if (!term) continue;

    const definition = (normalized["d"] ?? "").trim();
    const pronunciation = (normalized["p"] ?? "").trim();
    const memo = (normalized["e"] ?? "").trim();

    out.push(Term.makeSimpleTerm(term, definition, pronunciation, memo));
  }
  return out;
}

function hasTdpeHeader(fields: string[] | undefined | null): boolean {
  if (!fields || fields.length === 0) return false;

  const normalized = fields.map((h) => h.trim().toLowerCase()).filter(Boolean);

  const required = ["t", "d", "p", "e"];
  return required.every((k) => normalized.includes(k));
}
